import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

import { catchError, Observable, of, tap, throwError } from 'rxjs';

import { CoreResponse } from '../../../core/models/core-response.interface';
import { environment } from '@environment/environment';
import { AlertService } from '@sharedServices/alert.service';
import { User } from '@userModule/models/user.model';
import { ResetPasswordDto } from '@auth/interfaces/reset-password.dto';
import { ExceptionProvider } from '@providers/exception.provider';
import { ChangePasswordDto } from '@auth/interfaces/change-password.dto';
import { Operation } from '@auth/enums/operation.enum';
import { ChangeEmailDto } from '@auth/interfaces/change-email.dto';
import { UserStore } from '../stores/user.store';
import { RoleMenuPermissionService } from '@userModule/services/role-menu-permission.service';

/**
 * Service for handling authentication-related operations.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private _httpClient = inject(HttpClient);
  private _alertService = inject(AlertService);
  private _exceptionProvider = inject(ExceptionProvider);
  private _router = inject(Router);
  private _roleMenuPermissionService = inject(RoleMenuPermissionService);
  private _userStore = inject(UserStore);

  private API_PATH = `${environment.apiBaseUrl}/v1/auth`;

  /**
   * The current user.
   */
  user = this._userStore.user;
  /**
   * The permissions of the current user's role.
   */
  rolePermissions = this._userStore.rolePermissions;

  /**
   * Logs in a user.
   * @param email The user's email.
   * @param password The user's password.
   * @returns An observable with the core response.
   */
  login(email: string, password: string): Observable<CoreResponse<{ otpSended: Boolean; }>> {
    return this._httpClient.post<CoreResponse<{ otpSended: Boolean; }>>(
      `${this.API_PATH}/login`,
      { email, password },
      { withCredentials: true }
    ).pipe(
      catchError((error: HttpErrorResponse) => this._exceptionProvider.handleGenricError(error))
    );
  }

  /**
   * Logs out the current user.
   * @returns An observable with the core response.
   */
  logout(): Observable<CoreResponse<void>> {
    return this._httpClient.post<CoreResponse<void>>(`${this.API_PATH}/logout`, {}, { withCredentials: true }).pipe(
      tap((res) => {
        if (res.success) {
          this._userStore.clearUser();
          this._router.navigate(['login']);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this._userStore.clearUser();
        this._router.navigate(['login']);
        this._alertService.error(error.error.message);
        return throwError(() => error);
      })
    );
  }

  /**
   * Refreshes the access token.
   * @returns An observable with the core response.
   */
  refreshAccessToken(): Observable<CoreResponse<{ refreshed: boolean; }>> {
    return this._httpClient.post<CoreResponse<{ refreshed: boolean; }>>(
      `${this.API_PATH}/refresh-access-token`, {}, { withCredentials: true }
    ).pipe(
      catchError((error: HttpErrorResponse) => this._exceptionProvider.handleGenricError(error))
    );
  }

  /**
   * Sends a password recovery email.
   * @param email The user's email.
   * @returns An observable with the core response.
   */
  recoverPassword(email: string): Observable<CoreResponse<{ sended: boolean; }>> {
    return this._httpClient.post<CoreResponse<{ sended: boolean; }>>(`${this.API_PATH}/recover-password`, { email })
      .pipe(
        catchError((error: HttpErrorResponse) => this._exceptionProvider.handleGenricError(error))
      );
  }

  /**
   * Confirms a token.
   * @param token The token to confirm.
   * @returns An observable with the core response.
   */
  confirmToken(token: string): Observable<CoreResponse<{ operationId: number }>> {
    return this._httpClient.post<CoreResponse<{ operationId: number }>>(`${this.API_PATH}/confirm-token/${token}`, {})
      .pipe(
        catchError((error: HttpErrorResponse) => this._exceptionProvider.handleGenricError(error))
      );
  }

  /**
   * Resets the user's password.
   * @param resetPasswordDto The reset password data.
   * @returns An observable with the core response.
   */
  resetPassword(resetPasswordDto: ResetPasswordDto): Observable<CoreResponse<User>> {
    return this._httpClient.post<CoreResponse<User>>(`${this.API_PATH}/reset-password`, resetPasswordDto)
      .pipe(
        catchError((error: HttpErrorResponse) => this._exceptionProvider.handleGenricError(error))
      );
  }

  /**
   * Loads the current user's data.
   * @returns An observable with the user data or null.
   */
  loadUser(): Observable<CoreResponse<User> | null | User> {
    if (this.user()) {
      return of(this.user());
    }

    return this._httpClient.get<CoreResponse<User>>(`${this.API_PATH}/me`, { withCredentials: true })
      .pipe(
        tap((res) => {
          this._userStore.setUser(res.payload);
          this._getUserRolePermissions(this.user()!.role.id);
        }),
        catchError(() => {
          this._userStore.setUser(null);
          return of(null);
        })
      );
  }

  /**
   * Requests a password change.
   * @param changePasswordDto The change password data.
   * @returns An observable with the core response.
   */
  changePasswordRequest(changePasswordDto: ChangePasswordDto): Observable<CoreResponse<{ otpSended: boolean; }>> {
    return this._httpClient.post<CoreResponse<{ otpSended: boolean; }>>(
      `${this.API_PATH}/change-password-request`,
      changePasswordDto
    ).pipe(
      catchError((error: HttpErrorResponse) => this._exceptionProvider.handleGenricError(error))
    );
  }

  /**
   * Requests an email change.
   * @param changeEmailDto The change email data.
   * @returns An observable with the core response.
   */
  changeEmailRequest(changeEmailDto: ChangeEmailDto): Observable<CoreResponse<{ otpSended: boolean; }>> {
    return this._httpClient.post<CoreResponse<{ otpSended: boolean; }>>(
      `${this.API_PATH}/change-email-request`,
      changeEmailDto
    ).pipe(
      catchError((error: HttpErrorResponse) => this._exceptionProvider.handleGenricError(error))
    );
  }

  /**
   * Verifies an OTP code.
   * @param otp The OTP code.
   * @param operation The operation for which the OTP is being verified.
   * @param userId The user's ID (optional).
   * @returns An observable with the core response.
   */
  verifyOtp(otp: string, operation: Operation, userId?: string): Observable<CoreResponse<User>> {
    return this._httpClient.post<CoreResponse<User>>(`${this.API_PATH}/verify-otp`, { userId, otp, operation }).pipe(
      tap((res) => {
        if (res.success && Operation.LOGIN_2FA === operation) {
          this.loadUser().subscribe({
            next: () => {
              this._getUserRolePermissions(this.user()!.role.id);
              this._router.navigate(['/'])
            }
          });
        }
      }),
      catchError((error: HttpErrorResponse) => this._exceptionProvider.handleGenricError(error))
    );
  }

  /**
   * Checks if the user has a specific permission.
   * @param permissionKey The permission key.
   * @param menuItemId The menu item ID (optional).
   * @returns True if the user has the permission, false otherwise.
   */
  hasPermission(permissionKey: string, menuItemId?: number): boolean {
    return this.rolePermissions().some(rmp => rmp.permission.name === permissionKey && rmp.menuItem.id === menuItemId);
  }

  /**
   * Gets the user's role permissions.
   * @param roleId The user's role ID.
   */
  private _getUserRolePermissions(roleId: number): void {
    this._roleMenuPermissionService.findAllByRoleId(roleId).subscribe({
      next: (res) => {
        if (res.success) {
          this._userStore.setRolePermissions(res.payload);
        }
      }
    });
  }
}
